<script lang="ts" setup>
import RichTextField from '@/components/common/RichTextField.vue';
import CurrentLanguage from '@/components/CurrentLanguage.vue';
import MediaList from '@/components/media/MediaList.vue';
import { MediaPurpose } from '@/models/media.model';
import {
  LearningItem,
  LearningType,
  titleRules,
  descriptionRules,
  learningTypes,
} from '@/models/learning-item.model';
import { useEvents, Event } from '@/services/events.composable';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import { LearningActions, LearningState } from '@/stores/learning.store';
import { NEW_ID } from '@/util/constants';
import { computed, onMounted, PropType, ref, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import CategoriesField from '../categories/CategoriesField.vue';
import CategoryField from '../categories/CategoryField.vue';

const store = useStore<ShiftCMSState & { ms: LearningState }>();

const events = useEvents();

const props = defineProps({
  data: {
    type: Object as PropType<LearningItem>,
  },
});

const valid = ref<boolean>(false);

const learningItem = ref<LearningItem>({
  id: NEW_ID,
  title: '',
  author: '',
  url: '',
  duration: '',
  type: LearningType.Other,
  recommendation: '',
  recommended: false,
  published: false,
  languages: [],
  description: '',
  categories: [],
  media: [],
});

const action = computed<string>(() =>
  learningItem.value.id === NEW_ID ? 'Add' : 'Save',
);

const saveLearningItem = async () => {
  let success: boolean = false;
  if (valid.value) {
    if (learningItem.value.id === NEW_ID) {
      success = await store.dispatch(
        LearningActions.AddLearningItem,
        learningItem.value,
      );
    } else {
      success = await store.dispatch(
        LearningActions.UpdateLearningItem,
        learningItem.value,
      );
    }
  }
  if (success) {
    events.emit(Event.Saved, true);
    store.dispatch(Actions.ShowNotification, {
      message: 'Saved LearningItem',
      type: 'success',
    });
    store.commit(Mutations.HideDrawer);
    store.dispatch(LearningActions.GetLearningItems);
  } else {
    store.dispatch(Actions.ShowNotification, {
      message: 'Failed to save Learning Item',
      type: 'error',
    });
  }
};

const updateLanguage = async () => {
  if (learningItem.value.id === NEW_ID) {
    Object.assign(learningItem, { title: '' });
  } else {
    const cat = await store.dispatch(
      LearningActions.GetLearningItem,
      learningItem.value.id,
    );

    if (cat) {
      Object.assign(learningItem.value, { ...cat });
    }
  }
};

onBeforeUnmount(() => {
  events.off(Event.LanguageChanged, updateLanguage);
});

onMounted(() => {
  events.on(Event.LanguageChanged, updateLanguage);

  if (props.data) {
    Object.assign(learningItem.value, {
      ...props.data,
    });
  }
});

const removeLabel = async () => {
  if (
    learningItem.value &&
    learningItem.value.id !== NEW_ID &&
    confirm('Are you absolutely sure you want to delete this "LearningItem"?')
  ) {
    store.dispatch(LearningActions.DeleteLearningItem, learningItem.value.id);

    store.commit(Mutations.HideDrawer);
  }
};

const togglePublished = async (published: boolean) => {
  if (learningItem.value.id !== -1) {
    store.dispatch(LearningActions.PublishLearningItem, {
      id: learningItem.value.id,
      published,
    });
  }
  learningItem.value.published = published;
};

const languageOptions = [
  {
    title: 'English',
    value: 'en',
  },
  {
    title: 'Nederlands',
    value: 'nl',
  },
];

function checkUrl(url: string) {
  window.open(url, '_new');
}
</script>
<template>
  <v-form v-model="valid" @submit.prevent="saveLearningItem">
    <v-container>
      <h2>{{ $t(action) }} a learning item <CurrentLanguage /></h2>
      <v-row>
        <v-col cols="9">
          <v-text-field
            v-model="learningItem.title"
            :rules="titleRules"
            label="Title"
            hint="Not used on learningItem page. Use a top level header in the description"
            required
          ></v-text-field>
        </v-col>
        <v-col class="pt-6">
          <v-btn
            block
            :disabled="learningItem.id === NEW_ID"
            @click="() => togglePublished(!learningItem.published)"
            :color="!learningItem.published ? 'success' : 'warning'"
            >{{ learningItem.published ? 'Un-publish' : 'Publish' }}</v-btn
          >
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <v-text-field
            v-model="learningItem.author"
            label="Creator / Author"
            required
          ></v-text-field>
        </v-col>
        <v-col>
          <v-select
            v-model="learningItem.type"
            :items="learningTypes"
            label="Type"
            required
          ></v-select>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-text-field
            v-model="learningItem.duration"
            label="Duration"
            hint="Use simple notation in minutes (60, 90, 120)"
            required
          ></v-text-field>
        </v-col>
        <v-col>
          <v-select
            v-model="learningItem.languages"
            label="Languages"
            multiple
            chips
            :items="languageOptions"
            required
          ></v-select>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="9">
          <v-text-field
            v-model="learningItem.url"
            label="URL"
            required
          ></v-text-field>
        </v-col>
        <v-col class="pt-6">
          <v-btn
            block
            color="info"
            @click="checkUrl(learningItem.url)"
            :disabled="learningItem.url == ''"
            >Check</v-btn
          >
        </v-col>
      </v-row>

      <RichTextField
        label="Description"
        :height="200"
        :rules="descriptionRules"
        v-model="learningItem.description"
        :media="learningItem.media"
      />

      <CategoryField main disabled v-model="learningItem.mainCategory" />

      <CategoriesField v-model="learningItem.categories" />

      <RichTextField
        label="Recommendation"
        :height="200"
        v-model="learningItem.recommendation"
        :media="learningItem.media"
      />
      <v-checkbox
        label="Recommended"
        density="compact"
        v-model="learningItem.recommended"
      />

      <MediaList v-model="learningItem" :required="[MediaPurpose.Main]" />
      <v-row>
        <v-col col="2"></v-col>
        <v-col>
          <v-btn
            class="mt-2"
            type="button"
            color="warning"
            @click="removeLabel"
            block
            >Delete</v-btn
          >
        </v-col>
        <v-col>
          <v-btn class="mt-2" type="reset" block>Reset</v-btn>
        </v-col>
        <v-col>
          <v-btn class="mt-2" type="submit" color="success" block>{{
            action
          }}</v-btn>
        </v-col>
        <v-col col="2"></v-col>
      </v-row>
    </v-container>
  </v-form>
</template>
<style scoped>
pre {
  font-family: sans-serif;
  font-size: 12px;
  background-color: rgb(255, 230, 171);
  display: block;
  height: 100%;
  padding: 8px;
}
</style>
